import * as R from 'ramda';
import * as React from 'react';
import { useMemo, useState } from 'react';
import ProjectLayout from '../ProjectLayout';
import SurveyHeader from '../survey/SurveyHeader';
import { onMobileDevice, onPhone } from '../../globalStyle/mediaQueries';
import { css } from '@emotion/core';
import { useMsgText } from '../../localization/hooks';
import LocalDiv from '../../localization/LocalDiv';
import { useAppState } from '../../core/redux/hooks';
import { Str } from '../../localization/translationKeys';
import { ProjStr } from '../messages/ProjStr';
import ProjMsg from '../messages/ProjMsg';
import Goal from './Goal';
import EmailGoalsForm from './EmailGoalsForm';
import { useMount } from '../../core/hooks/useMount';
import { useDispatch } from 'react-redux';
import { goalsAnswersAction } from './goalsAnswersAction';
import { GoalAnswer } from './models';
import { setGoalAnswerAction } from './setGoalAnswerAction';
import { COLORS } from '../../globalStyle/colors';
import { FLEX_ROW, FLEX_COLUMN } from '../../layout/flexBox';
import { isNilOrEmpty } from '../../language/module';
import MobileSetGoalsActionsBar from './MobileSetGoalsActionsBar';
import { useWindowSize } from '../../core/hooks/useWindowSize';
import { useToggle } from '../../core/hooks/useToggle';
import SendGoalsEmailModal from './SendGoalsEmailModal';
import Card from '../../layout/card/Card';
import CardHeader from '../../layout/card/CardHeader';
import { faEnvelope } from '@fortawesome/free-solid-svg-icons';
import { useProjIds } from '../../navigation/hooks';


const styles = {
  header:    css`
    margin-bottom: 40px;
    ${onMobileDevice} {
      margin-bottom: 35px;
    }
  `,
  intro:     css`
    margin: 40px 0;
    display: block;
    ${onMobileDevice} {
      margin: 10px 0;
    }
  `,
  content:   css`
    color: ${COLORS.DARK_GREY};
  `,
  container: css`
    flex-wrap: wrap;
  `,
  goals:     css`
    flex-wrap: wrap;
    flex-grow: 1;
    flex-basis: 50%;
    ${onPhone} {
      margin: 0;
    }

    &[dir="ltr"] {
      margin-right: 25px;
    }
    
    &[dir="rtl"] {
      margin-left: 25px;
    }    
  `,


  card:      [
    FLEX_COLUMN,
    css`
      background-color: ${COLORS.LIGHTEST_GREY};
    `,
  ],
  cardTitle: [
    css`
      margin: 0 0 20px;
    `,
  ],
};


type Props = {
  className?: string;
};


/**
 * SetGoals: a component that renders the page where the user can set their project goals -- what they want to achieve
 * by getting feedback from others, completing their self-survey etc.
 *
 * PureComponent: true
 * Redux Connected: false
 */
const SetGoals: React.FC<Props> = props => {
  const dispatch = useDispatch();

  const { isMobile } = useWindowSize();

  const pageTitle = useMsgText(Str.SetGoals);
  const cardTitle = useMsgText(Str.EmailGoals);

  const [underEdit, setUnderEdit]            = useState<GoalAnswer | undefined>(undefined);
  const [sendingGoals, toggleSendGoalsModal] = useToggle();

  const projIds = useProjIds();

  const project    = useAppState(state => state.projectSummary);
  const languages  = useAppState(state => state.projectLanguages?.[`${projIds.projId}`]);
  const languageId = useAppState(state => state.locale.lang.languageId);

  useMount(() => { dispatch(goalsAnswersAction(projIds.projId, projIds.participantId, languageId)); });

  const goalsAndAnswers = useAppState(state => (state.goalsAnswers || {})[projIds.projId]);

  const allAnswered = useMemo(
    () => Object.values(goalsAndAnswers || {}).every(s => !isNilOrEmpty(s.questionAnswer)),
    [goalsAndAnswers],
  );

  const projDefaultLang = useMemo(
    () => languages?.find(lang => lang.default)?.languageId ?? languages?.[0]?.languageId ?? 1,
    [languages],
  );

  const saveAnswer = React.useCallback(
    (answer: string) => {
      dispatch(setGoalAnswerAction(projIds.projId, projIds.participantId, underEdit?.questionId || 0, answer));
      setUnderEdit(undefined);
    },
    [dispatch, projIds.projId, projIds.participantId, underEdit],
  );

  const handleClick = React.useCallback(
    (goalAnswer: GoalAnswer) => goalAnswer !== underEdit && setUnderEdit(goalAnswer),
    [setUnderEdit, underEdit],
  );

  const goalCards = useMemo(
    () => {
      const inEditMode   = (questionId: number) => underEdit?.questionId === questionId;
      const handleCancel = () => setUnderEdit(undefined);

      return R.map(
        (goal: GoalAnswer) => (
          <Goal
            key={goal.questionId}
            goalAnswer={goal}
            saveAnswer={saveAnswer}
            handleClick={() => handleClick(goal)}
            handleCancel={handleCancel}
            inEditMode={inEditMode(goal.questionId)}
          />
        ),
        Object.values(goalsAndAnswers ?? {}),
      );
    },
    [underEdit, goalsAndAnswers, saveAnswer, handleClick],
  );

  return (
    <ProjectLayout className={props.className}>
      <SurveyHeader title={`/ ${pageTitle}`} deadline={project?.surveyDeadline} css={styles.header} />
      <LocalDiv css={styles.content}>
        <ProjMsg str={ProjStr.SetGoalsIntro} css={styles.intro} />
        <div css={[FLEX_ROW, styles.container]}>
          <LocalDiv css={[FLEX_COLUMN, styles.goals]}>
            {goalCards}
          </LocalDiv>
          {
            !isMobile && (
              <Card css={styles.card} className={props.className}>
                <CardHeader title={cardTitle} icon={faEnvelope} css={styles.cardTitle} />
                <EmailGoalsForm
                  languageId={projDefaultLang}
                  allAnswered={allAnswered}
                />
              </Card>
            )
          }
        </div>
      </LocalDiv>
      {
        isMobile && (
          <MobileSetGoalsActionsBar
            allAnswered={allAnswered}
            languageId={projDefaultLang}
            openSendGoalsModal={toggleSendGoalsModal}
          />
        )
      }
      {
        sendingGoals && (
          <SendGoalsEmailModal
            allAnswered={allAnswered}
            languageId={projDefaultLang}
            dismiss={toggleSendGoalsModal}
          />
        )
      }
    </ProjectLayout>
  );
};

export default SetGoals;
